import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { AppShell } from '../../components/AppShell.js';
import { useIntegrationProducts } from '../../lib/adNetwork.hooks.js';
import { useAuthenticatedSession } from '../../lib/useAuthenticatedSession.js';
import { useShellAccount } from '../../lib/useShellAccount.js';

const SETTINGS_TAB_PATHS = {
  users: '/settings/users',
  integrations: '/settings/integrations',
  notifications: '/settings/notifications',
} as const;

function SettingsLayout(): ReactNode {
  const navigate = useNavigate();
  const { session, isPending, onSignOut } = useAuthenticatedSession();
  const account = useShellAccount(session?.user.email);
  const showIntegrations = (useIntegrationProducts().data?.length ?? 0) > 0;
  const activeTab = useLocation({
    select: (location): keyof typeof SETTINGS_TAB_PATHS => {
      if (location.pathname.startsWith('/settings/users')) return 'users';
      if (location.pathname.includes('/notifications')) return 'notifications';
      return 'integrations';
    },
  });

  if (isPending || session === null) {
    return (
      <Box
        sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AppShell active="settings" sx={{ p: 6 }} account={account} onLogout={() => void onSignOut()}>
      <Tabs
        // A hidden tab has no <Tab> to select; MUI warns on an unmatched value.
        value={activeTab === 'integrations' && !showIntegrations ? false : activeTab}
        aria-label="Settings sections"
        sx={{ mb: 6, borderBottom: '1px solid', borderColor: 'divider' }}
        onChange={(_, value: keyof typeof SETTINGS_TAB_PATHS) =>
          void navigate({ to: SETTINGS_TAB_PATHS[value] })
        }
      >
        <Tab value="users" label="Users" data-testid="settings-tab-users" />
        {showIntegrations && (
          <Tab value="integrations" label="Integrations" data-testid="settings-tab-integrations" />
        )}
        <Tab value="notifications" label="Notifications" data-testid="settings-tab-notifications" />
      </Tabs>
      <Outlet />
    </AppShell>
  );
}

export const Route = createFileRoute('/settings')({
  component: SettingsLayout,
});
